/**
 * The Inventory module's route table (CLAUDE.md §8.10).
 *
 * `src/router/index.ts` is shared by every module, so this file declares the
 * records and the router spreads `INVENTORY_ROUTES` in. Keeping them here means
 * the paths, the permissions and the components that implement them are decided
 * in one place by whoever owns the module.
 *
 * Every `:id` segment carries the `\d+` constraint, so no literal word can ever
 * be parsed as a document id, and the list route of each family is declared
 * before its detail route so the ordering says the same thing the constraint
 * enforces.
 *
 * Which permission guards which screen follows the API rather than the topic.
 * Reading an adjustment, a transfer or a cycle count is `inventory.stock.read`
 * (that is what `GET /adjustments`, `/stock-transfers` and `/cycle-counts`
 * declare); *acting* on one needs the narrower code, and the screens check that
 * separately. The reason-code screen is `inventory.item.read` because that is
 * what `GET /adjustment-reason-codes` declares — reason codes are master data,
 * not stock.
 *
 * `INVENTORY_PATHS` is the same set as strings, for links inside the module.
 * Links *out* of it — the journal a movement posted, the ledger behind an
 * inventory control account — are built here and then checked against the router
 * by `linkIfRoutable` before being rendered. GL owns those URLs, and a guessed
 * link that 404s in the middle of an auditor's evidence chain is worse than
 * plain text.
 */

import type { Router, RouteRecordRaw } from 'vue-router'

import { INVENTORY_PERMISSIONS } from '@/modules/inventory/permissions'

export const INVENTORY_ROUTES: RouteRecordRaw[] = [
  {
    path: '/inventory/items',
    name: 'inventory-items',
    component: () => import('@/modules/inventory/ItemsListView.vue'),
    meta: {
      titleKey: 'inventory.routeTitles.items',
      permission: INVENTORY_PERMISSIONS.itemRead,
    },
  },
  {
    path: '/inventory/items/:id(\\d+)',
    name: 'inventory-item',
    component: () => import('@/modules/inventory/ItemView.vue'),
    meta: {
      titleKey: 'inventory.routeTitles.item',
      permission: INVENTORY_PERMISSIONS.itemRead,
    },
  },

  {
    path: '/inventory/warehouses',
    name: 'inventory-warehouses',
    component: () => import('@/modules/inventory/WarehousesListView.vue'),
    meta: {
      titleKey: 'inventory.routeTitles.warehouses',
      permission: INVENTORY_PERMISSIONS.warehouseRead,
    },
  },
  {
    path: '/inventory/warehouses/:id(\\d+)',
    name: 'inventory-warehouse',
    component: () => import('@/modules/inventory/WarehouseView.vue'),
    meta: {
      titleKey: 'inventory.routeTitles.warehouse',
      permission: INVENTORY_PERMISSIONS.warehouseRead,
    },
  },

  {
    path: '/inventory/stock-levels',
    name: 'inventory-stock-levels',
    component: () => import('@/modules/inventory/StockLevelsView.vue'),
    meta: {
      titleKey: 'inventory.routeTitles.stockLevels',
      permission: INVENTORY_PERMISSIONS.stockRead,
    },
  },
  {
    path: '/inventory/stock-ledger',
    name: 'inventory-stock-ledger',
    component: () => import('@/modules/inventory/StockLedgerView.vue'),
    meta: {
      titleKey: 'inventory.routeTitles.stockLedger',
      permission: INVENTORY_PERMISSIONS.stockRead,
    },
  },

  {
    path: '/inventory/adjustments',
    name: 'inventory-adjustments',
    component: () => import('@/modules/inventory/AdjustmentsListView.vue'),
    meta: {
      titleKey: 'inventory.routeTitles.adjustments',
      permission: INVENTORY_PERMISSIONS.stockRead,
    },
  },
  {
    path: '/inventory/adjustments/:id(\\d+)',
    name: 'inventory-adjustment',
    component: () => import('@/modules/inventory/AdjustmentView.vue'),
    meta: {
      titleKey: 'inventory.routeTitles.adjustment',
      permission: INVENTORY_PERMISSIONS.stockRead,
    },
  },
  {
    path: '/inventory/reason-codes',
    name: 'inventory-reason-codes',
    component: () => import('@/modules/inventory/ReasonCodesView.vue'),
    meta: {
      titleKey: 'inventory.routeTitles.reasonCodes',
      permission: INVENTORY_PERMISSIONS.itemRead,
    },
  },

  {
    path: '/inventory/cycle-counts',
    name: 'inventory-cycle-counts',
    component: () => import('@/modules/inventory/CycleCountsListView.vue'),
    meta: {
      titleKey: 'inventory.routeTitles.cycleCounts',
      permission: INVENTORY_PERMISSIONS.stockRead,
    },
  },
  {
    path: '/inventory/cycle-counts/:id(\\d+)',
    name: 'inventory-cycle-count',
    component: () => import('@/modules/inventory/CycleCountView.vue'),
    meta: {
      titleKey: 'inventory.routeTitles.cycleCount',
      permission: INVENTORY_PERMISSIONS.stockRead,
    },
  },

  {
    path: '/inventory/transfers',
    name: 'inventory-transfers',
    component: () => import('@/modules/inventory/transfers/TransfersBoardView.vue'),
    meta: {
      titleKey: 'inventory.routeTitles.transfers',
      permission: INVENTORY_PERMISSIONS.stockRead,
    },
  },
  {
    path: '/inventory/transfers/:id(\\d+)',
    name: 'inventory-transfer',
    component: () => import('@/modules/inventory/transfers/TransferDocumentView.vue'),
    meta: {
      titleKey: 'inventory.routeTitles.transfer',
      permission: INVENTORY_PERMISSIONS.stockRead,
    },
  },

  {
    path: '/inventory/revaluations',
    name: 'inventory-revaluations',
    component: () => import('@/modules/inventory/RevaluationsView.vue'),
    meta: {
      titleKey: 'inventory.routeTitles.revaluations',
      permission: INVENTORY_PERMISSIONS.stockRead,
    },
  },
  {
    path: '/inventory/reorder-suggestions',
    name: 'inventory-reorder-suggestions',
    component: () => import('@/modules/inventory/ReorderSuggestionsView.vue'),
    meta: {
      titleKey: 'inventory.routeTitles.reorderSuggestions',
      permission: INVENTORY_PERMISSIONS.stockRead,
    },
  },

  {
    path: '/inventory/reports/valuation',
    name: 'inventory-valuation',
    component: () => import('@/modules/inventory/ValuationReportView.vue'),
    meta: {
      titleKey: 'inventory.routeTitles.valuation',
      permission: INVENTORY_PERMISSIONS.reportRead,
    },
  },

  /* ------------------------------------------------------------- C6 (§5.5) */

  {
    path: '/inventory/lots',
    name: 'inventory-lots',
    component: () => import('@/modules/inventory/lots/LotsListView.vue'),
    meta: { titleKey: 'inventory.routeTitles.lots', permission: INVENTORY_PERMISSIONS.lotRead },
  },
  {
    path: '/inventory/lots/:id(\\d+)',
    name: 'inventory-lot',
    component: () => import('@/modules/inventory/lots/LotView.vue'),
    meta: { titleKey: 'inventory.routeTitles.lot', permission: INVENTORY_PERMISSIONS.lotRead },
  },
  {
    path: '/inventory/serials',
    name: 'inventory-serials',
    component: () => import('@/modules/inventory/serials/SerialRegisterView.vue'),
    meta: {
      titleKey: 'inventory.routeTitles.serials',
      permission: INVENTORY_PERMISSIONS.stockRead,
    },
  },
  {
    path: '/inventory/recalls',
    name: 'inventory-recalls',
    component: () => import('@/modules/inventory/recalls/RecallsBoardView.vue'),
    meta: { titleKey: 'inventory.routeTitles.recalls', permission: INVENTORY_PERMISSIONS.recallRead },
  },
  {
    path: '/inventory/recalls/:id(\\d+)',
    name: 'inventory-recall',
    component: () => import('@/modules/inventory/recalls/RecallView.vue'),
    meta: { titleKey: 'inventory.routeTitles.recall', permission: INVENTORY_PERMISSIONS.recallRead },
  },

  {
    path: '/inventory/warehouse-layout',
    name: 'inventory-warehouse-layout',
    component: () => import('@/modules/inventory/wms/WarehouseLayoutView.vue'),
    meta: { titleKey: 'inventory.routeTitles.warehouseLayout', permission: INVENTORY_PERMISSIONS.zoneRead },
  },
  {
    path: '/inventory/settings',
    name: 'inventory-settings',
    component: () => import('@/modules/inventory/wms/InventorySettingsView.vue'),
    meta: { titleKey: 'inventory.routeTitles.settings', permission: INVENTORY_PERMISSIONS.settingsRead },
  },

  {
    path: '/inventory/reservations',
    name: 'inventory-reservations',
    component: () => import('@/modules/inventory/allocation/ReservationsBoardView.vue'),
    meta: { titleKey: 'inventory.routeTitles.reservations', permission: INVENTORY_PERMISSIONS.stockRead },
  },
  {
    path: '/inventory/shortages',
    name: 'inventory-shortages',
    component: () => import('@/modules/inventory/allocation/ShortagesInboxView.vue'),
    meta: { titleKey: 'inventory.routeTitles.shortages', permission: INVENTORY_PERMISSIONS.stockRead },
  },
  {
    path: '/inventory/substitutes',
    name: 'inventory-substitutes',
    component: () => import('@/modules/inventory/allocation/SubstitutesView.vue'),
    meta: { titleKey: 'inventory.routeTitles.substitutes', permission: INVENTORY_PERMISSIONS.itemRead },
  },

  {
    path: '/inventory/cycle-counts/:id(\\d+)/review',
    name: 'inventory-count-review',
    component: () => import('@/modules/inventory/counting/CountReviewView.vue'),
    meta: { titleKey: 'inventory.routeTitles.countReview', permission: INVENTORY_PERMISSIONS.stockRead },
  },

  {
    path: '/inventory/par-levels',
    name: 'inventory-par-levels',
    component: () => import('@/modules/inventory/outlet/ParLevelsView.vue'),
    meta: { titleKey: 'inventory.routeTitles.parLevels', permission: INVENTORY_PERMISSIONS.parRead },
  },
  {
    path: '/inventory/requisitions',
    name: 'inventory-requisitions',
    component: () => import('@/modules/inventory/outlet/RequisitionsListView.vue'),
    meta: {
      titleKey: 'inventory.routeTitles.requisitions',
      permission: INVENTORY_PERMISSIONS.requisitionRead,
    },
  },
  {
    path: '/inventory/requisitions/:id(\\d+)',
    name: 'inventory-requisition',
    component: () => import('@/modules/inventory/outlet/RequisitionView.vue'),
    meta: {
      titleKey: 'inventory.routeTitles.requisition',
      permission: INVENTORY_PERMISSIONS.requisitionRead,
    },
  },
  {
    path: '/inventory/waste',
    name: 'inventory-waste',
    component: () => import('@/modules/inventory/outlet/WasteView.vue'),
    meta: { titleKey: 'inventory.routeTitles.waste', permission: INVENTORY_PERMISSIONS.stockRead },
  },
  {
    path: '/inventory/reports/shrinkage',
    name: 'inventory-shrinkage',
    component: () => import('@/modules/inventory/outlet/ShrinkageReportView.vue'),
    meta: { titleKey: 'inventory.routeTitles.shrinkage', permission: INVENTORY_PERMISSIONS.reportRead },
  },

  {
    path: '/inventory/recipes',
    name: 'inventory-recipes',
    component: () => import('@/modules/inventory/recipes/RecipesListView.vue'),
    meta: { titleKey: 'inventory.routeTitles.recipes', permission: INVENTORY_PERMISSIONS.recipeRead },
  },
  {
    path: '/inventory/recipes/:id(\\d+)',
    name: 'inventory-recipe',
    component: () => import('@/modules/inventory/recipes/RecipeView.vue'),
    meta: { titleKey: 'inventory.routeTitles.recipe', permission: INVENTORY_PERMISSIONS.recipeRead },
  },
  {
    path: '/inventory/usage-variance',
    name: 'inventory-usage-variance',
    component: () => import('@/modules/inventory/recipes/UsageVarianceView.vue'),
    meta: {
      titleKey: 'inventory.routeTitles.usageVariance',
      permission: INVENTORY_PERMISSIONS.recipeRead,
    },
  },
  {
    path: '/inventory/depletions',
    name: 'inventory-depletions',
    component: () => import('@/modules/inventory/recipes/DepletionsView.vue'),
    meta: {
      titleKey: 'inventory.routeTitles.depletions',
      permission: INVENTORY_PERMISSIONS.recipeRead,
    },
  },
  {
    path: '/inventory/haccp',
    name: 'inventory-haccp',
    component: () => import('@/modules/inventory/haccp/HACCPView.vue'),
    meta: { titleKey: 'inventory.routeTitles.haccp', permission: INVENTORY_PERMISSIONS.haccpRead },
  },

  {
    path: '/inventory/landed-cost',
    name: 'inventory-shipments',
    component: () => import('@/modules/inventory/landed-cost/ShipmentsListView.vue'),
    meta: {
      titleKey: 'inventory.routeTitles.shipments',
      permission: INVENTORY_PERMISSIONS.landedCostRead,
    },
  },
  {
    path: '/inventory/landed-cost/setup',
    name: 'inventory-landed-cost-setup',
    component: () => import('@/modules/inventory/landed-cost/LandedCostSetupView.vue'),
    meta: {
      titleKey: 'inventory.routeTitles.landedCostSetup',
      permission: INVENTORY_PERMISSIONS.landedCostRead,
    },
  },
  {
    path: '/inventory/landed-cost/:id(\\d+)',
    name: 'inventory-shipment',
    component: () => import('@/modules/inventory/landed-cost/ShipmentView.vue'),
    meta: {
      titleKey: 'inventory.routeTitles.shipment',
      permission: INVENTORY_PERMISSIONS.landedCostRead,
    },
  },
  {
    path: '/inventory/reports/landed-cost-aging',
    name: 'inventory-landed-cost-aging',
    component: () => import('@/modules/inventory/landed-cost/LandedCostAgingView.vue'),
    meta: {
      titleKey: 'inventory.routeTitles.landedCostAging',
      permission: INVENTORY_PERMISSIONS.reportRead,
    },
  },

  {
    path: '/inventory/replenishment',
    name: 'inventory-replenishment',
    component: () => import('@/modules/inventory/replenishment/ReplenishmentView.vue'),
    meta: {
      titleKey: 'inventory.routeTitles.replenishment',
      permission: INVENTORY_PERMISSIONS.stockRead,
    },
  },
  {
    path: '/inventory/classification',
    name: 'inventory-classification',
    component: () => import('@/modules/inventory/replenishment/ClassificationView.vue'),
    meta: {
      titleKey: 'inventory.routeTitles.classification',
      permission: INVENTORY_PERMISSIONS.reportRead,
    },
  },
  {
    path: '/inventory/recon',
    name: 'inventory-recon',
    component: () => import('@/modules/inventory/recon/ReconRunsView.vue'),
    meta: { titleKey: 'inventory.routeTitles.recon', permission: INVENTORY_PERMISSIONS.reconRead },
  },
  {
    path: '/inventory/recon/:id(\\d+)',
    name: 'inventory-recon-run',
    component: () => import('@/modules/inventory/recon/ReconRunView.vue'),
    meta: { titleKey: 'inventory.routeTitles.reconRun', permission: INVENTORY_PERMISSIONS.reconRead },
  },
]

/* ------------------------------------------------------------ own screens */

export const INVENTORY_PATHS = {
  items: '/inventory/items',
  item: (id: number) => `/inventory/items/${id}`,
  warehouses: '/inventory/warehouses',
  warehouse: (id: number) => `/inventory/warehouses/${id}`,
  stockLevels: '/inventory/stock-levels',
  stockLedger: '/inventory/stock-ledger',
  adjustments: '/inventory/adjustments',
  adjustment: (id: number) => `/inventory/adjustments/${id}`,
  reasonCodes: '/inventory/reason-codes',
  cycleCounts: '/inventory/cycle-counts',
  cycleCount: (id: number) => `/inventory/cycle-counts/${id}`,
  transfers: '/inventory/transfers',
  transfer: (id: number) => `/inventory/transfers/${id}`,
  revaluations: '/inventory/revaluations',
  reorderSuggestions: '/inventory/reorder-suggestions',
  valuation: '/inventory/reports/valuation',

  /* C6 (§5.5). */
  lots: '/inventory/lots',
  lot: (id: number) => `/inventory/lots/${id}`,
  expiringLots: '/inventory/lots?mode=expiring',
  serials: '/inventory/serials',
  recalls: '/inventory/recalls',
  recall: (id: number) => `/inventory/recalls/${id}`,
  warehouseLayout: '/inventory/warehouse-layout',
  inventorySettings: '/inventory/settings',
  reservations: '/inventory/reservations',
  shortages: '/inventory/shortages',
  substitutes: '/inventory/substitutes',
  countReview: (id: number) => `/inventory/cycle-counts/${id}/review`,
  parLevels: '/inventory/par-levels',
  requisitions: '/inventory/requisitions',
  requisition: (id: number) => `/inventory/requisitions/${id}`,
  waste: '/inventory/waste',
  shrinkage: '/inventory/reports/shrinkage',
  recipes: '/inventory/recipes',
  recipe: (id: number) => `/inventory/recipes/${id}`,
  usageVariance: '/inventory/usage-variance',
  depletions: '/inventory/depletions',
  haccp: '/inventory/haccp',
  shipments: '/inventory/landed-cost',
  shipment: (id: number) => `/inventory/landed-cost/${id}`,
  landedCostElements: '/inventory/landed-cost/setup',
  landedCostAging: '/inventory/reports/landed-cost-aging',
  replenishment: '/inventory/replenishment',
  classification: '/inventory/classification',
  recon: '/inventory/recon',
  reconRun: (id: number) => `/inventory/recon/${id}`,
} as const

/**
 * The stock ledger, pre-filtered — what an on-hand row, an item and a valuation
 * account all drill through to.
 *
 * The **running balance** the ledger carries is per (item, warehouse): every row
 * holds the balance of its own pair at that moment, so a mixed list interleaves
 * several unrelated running balances. This helper is how every caller inside the
 * module opens the ledger already narrowed to one pair, which is the only state
 * in which that column reads as a balance rather than as noise.
 */
export function stockLedgerRoute(params: {
  itemId?: number | null
  warehouseId?: number | null
  docType?: string
  docId?: number | null
  from?: string
  to?: string
}): string {
  const query = new URLSearchParams()
  if (params.itemId !== null && params.itemId !== undefined) {
    query.set('itemId', String(params.itemId))
  }
  if (params.warehouseId !== null && params.warehouseId !== undefined) {
    query.set('warehouseId', String(params.warehouseId))
  }
  if (params.docType !== undefined && params.docType !== '') query.set('docType', params.docType)
  if (params.docId !== null && params.docId !== undefined) query.set('docId', String(params.docId))
  if (params.from !== undefined && params.from !== '') query.set('from', params.from)
  if (params.to !== undefined && params.to !== '') query.set('to', params.to)
  const qs = query.toString()
  return qs === '' ? INVENTORY_PATHS.stockLedger : `${INVENTORY_PATHS.stockLedger}?${qs}`
}

/** Stock levels, narrowed to one item or one warehouse. */
export function stockLevelsRoute(params: {
  itemId?: number | null
  warehouseId?: number | null
}): string {
  const query = new URLSearchParams()
  if (params.itemId !== null && params.itemId !== undefined) {
    query.set('itemId', String(params.itemId))
  }
  if (params.warehouseId !== null && params.warehouseId !== undefined) {
    query.set('warehouseId', String(params.warehouseId))
  }
  const qs = query.toString()
  return qs === '' ? INVENTORY_PATHS.stockLevels : `${INVENTORY_PATHS.stockLevels}?${qs}`
}

/* -------------------------------------------------------- links elsewhere */

/**
 * The journal a movement posted.
 *
 * Every row on the stock ledger except a same-company transfer leg carries a
 * `journalEntryId`, and this is the hop from the movement to the double entry it
 * produced — the drill-down chain §8.2 requires, made real from the inventory
 * end.
 */
export function journalRoute(entryId: number): string {
  return `/gl/journal-entries/${entryId}`
}

/**
 * The general ledger for one account — the hop from the valuation report's
 * control-account reconciliation to the postings that produced the balance it
 * disagrees with.
 */
export function ledgerRoute(companyId: number, accountId: number, to?: string): string {
  const query = new URLSearchParams({
    companyId: String(companyId),
    accountId: String(accountId),
  })
  if (to !== undefined && to !== '') query.set('to', to)
  return `/gl/reports/ledger?${query.toString()}`
}

/**
 * The path, but only if this app actually has a screen for it.
 *
 * The router's catch-all matches everything, so "resolves" is not the question —
 * "resolves to something other than the not-found page" is. A path the router
 * would send to the 404 screen comes back as null and the caller renders text.
 */
export function linkIfRoutable(router: Router, path: string | null): string | null {
  if (path === null || path === '') return null
  try {
    const resolved = router.resolve(path)
    if (resolved.matched.length === 0) return null
    if (resolved.matched.every((record) => record.name === 'not-found')) return null
    return path
  } catch {
    // An unparseable path is not a link either.
    return null
  }
}
